import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

type Filter = "all" | "draft" | "inReview" | "approved" | "published" | "archived";

export default function SolutionsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filter, setFilter] = useState<Filter>("all");
  const [filtersOpen, setFiltersOpen] = useState(true);

  const params = useMemo(() => {
    const map: Record<Filter, Record<string, unknown>> = {
      all:       {},
      draft:     { status: "DRAFT" },
      inReview:  { status: "IN_REVIEW" },
      approved:  { status: "APPROVED" },
      published: { status: "PUBLISHED" },
      archived:  { status: "ARCHIVED" },
    };
    return map[filter];
  }, [filter]);

  const { data, isLoading } = useList<any>("/solutions", { q, page, pageSize, sortBy, sortDir, ...params });
  const rows = data?.data ?? [];
  const total = data?.pagination?.total;

  async function del(id: string, name: string) {
    if (!confirm(`Delete solution "${name}"?`)) return;
    await api.delete(`/solutions/${id}`);
    qc.invalidateQueries({ queryKey: ["/solutions"] });
  }

  const groups: FilterGroup[] = [
    {
      title: "Status",
      items: [
        { label: "All IT solutions", active: filter === "all",     onClick: () => setFilter("all"), count: total },
        { label: "Draft",         active: filter === "draft",     onClick: () => setFilter("draft") },
        { label: "In Review",     active: filter === "inReview",  onClick: () => setFilter("inReview") },
        { label: "Approved",      active: filter === "approved",  onClick: () => setFilter("approved") },
        { label: "Published",     active: filter === "published", onClick: () => setFilter("published") },
        { label: "Archived",      active: filter === "archived",  onClick: () => setFilter("archived") },
      ],
    },
  ];

  return (
    <>
      <PageHeader
        title="IT Solutions"
        description="Service offerings (Data & AI, Cloud, Staffing, Government) and knowledge-base entries."
        actions={<Button asChild><Link to="/solutions/new"><Plus className="mr-1 h-4 w-4"/>Create IT Solution</Link></Button>}
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "createdAt", label: "Created" },
          { value: "name",      label: "Title" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={<Input placeholder="Search IT solutions…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />}
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Title</Th>
                  <Th>Category</Th>
                  <Th>Product</Th>
                  <Th className="text-right">Typical price</Th>
                  <Th>Status</Th>
                  <Th>Owner</Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={8} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={8} className="py-12 text-center text-muted-foreground">No IT solutions yet.</Td></Tr>
                )}
                {rows.map((s: any) => (
                  <Tr key={s.id} className="group">
                    <Td className="font-mono text-xs text-muted-foreground">{s.solutionNumber ?? "—"}</Td>
                    <Td>
                      <Link to={`/solutions/${s.id}`} className="font-medium hover:underline">{s.name}</Link>
                      {s.published && <Badge variant="secondary" className="ml-2 text-[10px]">Published</Badge>}
                    </Td>
                    <Td>{s.category ?? "—"}</Td>
                    <Td className="text-muted-foreground">{s.product?.name ?? "—"}</Td>
                    <Td className="text-right tabular-nums">
                      {s.typicalPrice != null ? `$${Number(s.typicalPrice).toLocaleString()}` : "—"}
                    </Td>
                    <Td><Badge variant="secondary">{(s.status ?? "DRAFT").replace(/_/g, " ")}</Badge></Td>
                    <Td className="text-muted-foreground">{s.owner?.name ?? "—"}</Td>
                    <Td>
                      <Button variant="ghost" size="icon" onClick={() => del(s.id, s.name)} aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>
    </>
  );
}
